import { Chain, Environment } from '@shared/data-access/interfaces';
import { map, take } from 'rxjs/operators';
import { AddressFormType, RadarGeocodeLayer } from '@cart-checkout/checkout/data-access/enums';
import { CheckoutErrorLogKeys } from '@cart-checkout/data-access/enums';
import { DeviceService, UtilityService } from '@shared/utils/services';
import {Injectable} from '@angular/core';
import { NgModelGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { CheckoutAnalyticsService } from '@cart-checkout/data-access/services';

@Injectable({
	providedIn: 'root'
})
export class AddressTypeaheadService {
	constructor(
		private utilityService: UtilityService,
		private environment: Environment,
		private checkoutAnalyticsService: CheckoutAnalyticsService,
		private deviceService: DeviceService,
		private chain: Chain
	) {}

	/**
	 * @description
	 * Configures auto complete settings based on athlete location
	 * If athlete is sharing geolocation then we will use that to narrow down search results
	 * Otherwise Radar will use athlete IP address to do so
	 *
	 * @param inputElement -- The HTML Input element to attach autocomplete
	 * @param addressForm -- The ngModelGroup form the provided input element is from
	 * @param formName - Form typeahead is used on, needed for analytics
	 */
	public setupAutocompleteListener(inputElement: HTMLInputElement, addressForm: NgModelGroup, formName: AddressFormType): void {
		if (!this.environment.apiTokens.radar) {
			this.checkoutAnalyticsService.generateLogError(this.getLogKey(formName), `Radar:Typeahead:Error on Form ${formName}: Missing radar publishable key`);
			return;
		}
		
		if (typeof window !== 'undefined') { // Prevents SSR issues
			import('radar-sdk-js').then((Radar) => {
				Radar.default.initialize(this.environment.apiTokens.radar[this.chain.chainIdentifierAbbr].publishableKey);

		// Configuration docs: https://radar.com/documentation/maps/autocomplete#configuration
		const autoCompleteSettings = {
			container: inputElement, // The container the autocomplete input will be rendered into. Can specify the html id, or DOM object
			debounceMS: 250, // The number of milliseconds to wait after typing is complete to refresh the result list
			threshold: 2, // Minimum number of characters that need to be typed before fetching results
			expandUnits: true, // Whether to include unit numbers in the results.
			countryCode: 'US', // 2-letter country code to filter results by needed for expandedUnits
			limit: this.deviceService.isMobileBreakpoint() ? 3 : 5, // Maximum number of results to show
			layers: [RadarGeocodeLayer.ADDRESS], // Filter results by layer type. See https://radar.com/documentation/api#query-parameters-3 for more info.
			onSelection: (autoCompleteRes) => {
				const addressVal = `${autoCompleteRes.number} ${autoCompleteRes.street}`;
				const address2Val = autoCompleteRes.unit;
				const zipcodeVal = autoCompleteRes.postalCode;

				inputElement.value = addressVal;
				addressForm.control.controls.address.setValue(addressVal, { emitEvent: true });
				addressForm.control.controls.zipcode.setValue(zipcodeVal, { emitEvent: true });

				if (address2Val) {
					addressForm.control.controls.address2.setValue(address2Val, { emitEvent: true });
				}

				this.checkoutAnalyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', { event: 'Athlete:Selected:Typeahead:Address', value: `Form: ${formName}` });
			},
			onError: (err) => {
				this.checkoutAnalyticsService.generateLogError(this.getLogKey(formName), `Radar:Typeahead:Error on Form ${formName}: ${err}`, err);
			}
		};
		this.fetchUserGeolocation()
			.subscribe(
				(latlong: {latitude: number, longitude: number}) => {
					autoCompleteSettings['near'] = latlong; // Use geolocation to narrow down athletes results
					Radar.default.ui.autocomplete(autoCompleteSettings);
				},
				() => {
					// If geolocation isn't used radar will attempt to narrow down results by the athletes ip address
					Radar.default.ui.autocomplete(autoCompleteSettings);
				});
			}
			);
		}
		
	}

	/**
	 * @description
	 * Attempts to acquire athletes lat/long from browser
	 * @returns Observable containing lat/long object
	 */
	private fetchUserGeolocation(): Observable<{ latitude: number, longitude: number }> {
		return this.utilityService
			.getCurrentGeolocationPosition()
			.pipe(
				take(1),
				map((geolocationPosition: GeolocationPosition) => {
					return {latitude: geolocationPosition.coords.latitude, longitude: geolocationPosition.coords.longitude};
				})
			);
	}

	/**
	 * @description
	 * Returns error log keys (used to search in kibana) by form name
	 * @returns CheckoutErrorLogKeys
	 */
	private getLogKey(formName: AddressFormType): CheckoutErrorLogKeys {
		switch(formName) {
			case AddressFormType.BILLING:
				return CheckoutErrorLogKeys.singlePageBilling;

			case AddressFormType.SHIPPING:
				return CheckoutErrorLogKeys.singlePageShipping;

			case AddressFormType.NEW_SAVED:
				return CheckoutErrorLogKeys.singlePageSavedAddresses;
		}

	}
}
